import {
    editTitle,
    editReminder,
    setReminder,
    removeReminder,
    editDueDate,
    setDueDate,
    removeDueDate,
    deleteTask,
} from './getTodoContextMenuItems.locstring.json';
import {
    markAsNotCompleted,
    markAsCompleted,
    removeImportance,
    markAsImportant,
} from '../strings.locstring.json';
import loc from 'owa-localize';
import getReorderContextMenuItem from './getReorderContextMenuItem';
import isTaskComplete from './isTaskComplete';
import selectTodoById from '../selectors/selectTodoById';
import isImportant from '../utils/isImportant';
import { ContextualMenuItemType, IContextualMenuItem } from 'office-ui-fabric-react/lib';

import {
    deleteTodo,
    markTodoComplete,
    updateTodoReminder,
    markTodoIncomplete,
    updateTodoDueDate,
} from '../actions/todoActions';
import {
    clickEditTodoContextMenuItem,
    clickRemoveImportanceContextMenuItem,
    clickMarkImportantContextMenuItem,
} from '../actions/todoContextMenuActions';

export default function getTodoContextMenuItems(
    itemId: string,
    renderReminderPicker?: () => JSX.Element,
    renderDueDatePicker?: () => JSX.Element,
    canReorder: boolean = false
): IContextualMenuItem[] {
    const todo = selectTodoById(itemId);
    if (!todo) {
        return [];
    }

    let dividerIndex = 0;
    const { Status, IsReminderOn, Importance, ReminderDateTime, DueDateTime } = todo;
    const isComplete = isTaskComplete(Status);
    const important = isImportant(Importance);
    const hasDueDate = DueDateTime != null; // Intentionally using != so we check for null and undefined
    const hasReminder = IsReminderOn && ReminderDateTime != null; // Intentionally using != so we check for null and undefined
    let menuItems = [
        getMarkCompleteMenuItem(itemId, isComplete),
        getEditSubjectMenuItem(itemId),
        getEditImportanceMenuItem(itemId, important),
        getDividerContextMenu(dividerIndex++),
        getSetReminderMenuItem(hasReminder, renderReminderPicker),
        hasReminder ? getRemoveReminderMenuItem(itemId) : null,
        hasReminder || hasDueDate ? getDividerContextMenu(dividerIndex++) : null,
        getSetDueDateMenuItem(hasDueDate, renderDueDatePicker),
        hasDueDate ? getRemoveDueDateMenuItem(itemId) : null,
        canReorder ? getReorderContextMenuItem(itemId) : null,
        getDividerContextMenu(dividerIndex++),
        getDeleteMenuItem(itemId),
    ];
    return menuItems.filter(m => m != null);
}

function getMarkCompleteMenuItem(itemId: string, isComplete: boolean): IContextualMenuItem {
    return {
        key: isComplete ? 'mark_task_incomplete' : 'mark_task_complete',
        name: isComplete ? loc(markAsNotCompleted) : loc(markAsCompleted),
        onClick: () => {
            isComplete ? markTodoIncomplete(itemId) : markTodoComplete(itemId);
        },
    };
}

function getEditSubjectMenuItem(itemId: string): IContextualMenuItem {
    return {
        key: 'start_editing_task',
        name: loc(editTitle),
        onClick: () => {
            clickEditTodoContextMenuItem(itemId);
        },
    };
}

function getEditImportanceMenuItem(itemId: string, isImportant: boolean): IContextualMenuItem {
    return isImportant ? getRemoveImportanceMenuItem(itemId) : getAddImportanceMenuItem(itemId);
}

function getRemoveImportanceMenuItem(itemId: string): IContextualMenuItem {
    return {
        key: 'remove_importance',
        name: loc(removeImportance),
        onClick: () => {
            clickRemoveImportanceContextMenuItem(itemId);
        },
    };
}

function getAddImportanceMenuItem(itemId: string): IContextualMenuItem {
    return {
        key: 'mark_as_important',
        name: loc(markAsImportant),
        onClick: () => {
            clickMarkImportantContextMenuItem(itemId);
        },
    };
}

function getSetReminderMenuItem(
    hasReminder: boolean,
    renderReminderPicker: () => JSX.Element
): IContextualMenuItem {
    return {
        key: 'set_reminder',
        name: hasReminder ? loc(editReminder) : loc(setReminder),
        subMenuProps: {
            items: [
                {
                    key: 'reminderPicker',
                    name: 'reminderPicker',
                    onRender: renderReminderPicker,
                },
            ],
        },
    };
}

function getRemoveReminderMenuItem(itemId: string): IContextualMenuItem {
    return {
        key: 'remove_reminder',
        name: loc(removeReminder),
        onClick: () => {
            updateTodoReminder(itemId, null);
        },
    };
}

function getSetDueDateMenuItem(
    hasDueDate: boolean,
    renderDueDatePicker: () => JSX.Element
): IContextualMenuItem {
    return {
        key: 'set_dueDate',
        name: hasDueDate ? loc(editDueDate) : loc(setDueDate),
        subMenuProps: {
            items: [
                {
                    key: 'dueDatePicker',
                    name: 'dueDatePicker',
                    onRender: renderDueDatePicker,
                },
            ],
        },
    };
}

function getRemoveDueDateMenuItem(itemId: string): IContextualMenuItem {
    return {
        key: 'remove_dueDate',
        name: loc(removeDueDate),
        onClick: () => {
            updateTodoDueDate(itemId, null);
        },
    };
}

function getDeleteMenuItem(itemId: string): IContextualMenuItem {
    return {
        key: 'delete_task',
        name: loc(deleteTask),
        onClick: () => {
            deleteTodo(itemId);
        },
    };
}

function getDividerContextMenu(dividerIndex: number): IContextualMenuItem {
    return {
        key: 'divider_' + dividerIndex,
        itemType: ContextualMenuItemType.Divider,
    };
}
